import React, { useCallback, useEffect, useState } from "react";
import { MessageBar, MessageBarType, Pivot, PivotItem, Spinner, SpinnerSize } from "@fluentui/react";
import { useAuth } from "../../auth/authProvider";
import { getUserInfoFromPostgresqlApi } from "../../api/api";
import {
    listRagStores,
    saveConversation,
    getConversation,
    startFileSearchQueryJob,
    getFileSearchQueryJobStatus,
    AtaQueryMode,
    QueryJobStepState,
    AtaModel,
    DEFAULT_ATA_MODEL
} from "../../services/ataService";
import { ChatMessage, RagStore, UserPermissions } from "../ata/types";
import AtaChatInterface from "./components/AtaChatInterface";
import AtaManagementView from "./components/AtaManagementView";
import AtaHistoryPanel from "./components/AtaHistoryPanel";
import styles from "./AtaPage.module.css";

const AtaPage: React.FC = () => {
    const { getAuthToken } = useAuth();
    const [token, setToken] = useState<string | undefined>(undefined);
    const [permissions, setPermissions] = useState<UserPermissions | null>(null);
    const [loadingAuth, setLoadingAuth] = useState(true);

    // Chat state
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    const [isQuerying, setIsQuerying] = useState(false);
    const [queryStageLabel, setQueryStageLabel] = useState<string | undefined>(undefined);
    const [queryStepStates, setQueryStepStates] = useState<QueryJobStepState[]>([]);
    const [queryMode, setQueryMode] = useState<AtaQueryMode>("question");
    const [queryModel, setQueryModel] = useState<AtaModel>(DEFAULT_ATA_MODEL);

    // Conversation history persistence
    const [activeConversationId, setActiveConversationId] = useState<number | undefined>(undefined);
    const [lastSavedAt, setLastSavedAt] = useState<string | undefined>(undefined);

    // Stores for chat source selection
    const [allStores, setAllStores] = useState<RagStore[]>([]);
    const [selectedStoreNames, setSelectedStoreNames] = useState<string[]>([]);
    const [selectedDocumentNames, setSelectedDocumentNames] = useState<string[]>([]);

    const [historyCollapsed, setHistoryCollapsed] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Init auth + user info
    useEffect(() => {
        (async () => {
            try {
                const t = await getAuthToken();
                setToken(t ?? undefined);
                if (t) {
                    const info = await getUserInfoFromPostgresqlApi(t);
                    if (info) {
                        setPermissions({
                            isDev: info.isDev,
                            canManageDocuments: info.can_manage_documents,
                            categories: info.categories,
                            categories_google: info.categories_google
                        });
                    }
                }
            } catch (e: any) {
                setError("Impossible d'initialiser la session : " + e.message);
            } finally {
                setLoadingAuth(false);
            }
        })();
    }, [getAuthToken]);

    // Load stores + their documents for the Sources panel
    const loadStores = useCallback(async () => {
        if (!token) return;
        try {
            // Single request: backend fetches all document lists in parallel server-side
            let stores = await listRagStores(token, true);
            if (!permissions?.isDev && permissions?.categories_google?.length) {
                const allowed = permissions.categories_google.map(c => c.toLowerCase());
                stores = stores.filter(s => allowed.includes(s.displayName.toLowerCase()) || (s.name && allowed.includes(s.name.toLowerCase())));
            }
            setAllStores(stores);
        } catch (e: any) {
            setError("Impossible de charger les catégories : " + e.message);
        }
    }, [token, permissions]);

    useEffect(() => {
        if (!loadingAuth) loadStores();
    }, [loadingAuth, loadStores]);

    const handleToggleStore = (storeName: string) => {
        const isRemoving = selectedStoreNames.includes(storeName);
        setSelectedStoreNames(prev => (isRemoving ? prev.filter(n => n !== storeName) : [...prev, storeName]));
        // Also select / deselect all documents belonging to this store
        const store = allStores.find(s => s.name === storeName);
        const docNames = (store?.documents ?? []).map(d => d.name);
        if (docNames.length > 0) {
            setSelectedDocumentNames(prev => (isRemoving ? prev.filter(n => !docNames.includes(n)) : [...prev, ...docNames.filter(n => !prev.includes(n))]));
        }
    };

    const handleToggleDocument = (documentName: string) => {
        setSelectedDocumentNames(prev => (prev.includes(documentName) ? prev.filter(n => n !== documentName) : [...prev, documentName]));
    };

    const handleSendMessage = async (query: string) => {
        if (!query.trim()) return;
        const userMsg: ChatMessage = { role: "user", parts: [{ text: query }] };
        // Capture history before the new message is added to state
        const currentHistory = chatHistory.map(msg => ({
            role: msg.role,
            text: msg.parts[0]?.text ?? ""
        }));
        const nextHistory = [...chatHistory, userMsg];
        setChatHistory(nextHistory);
        setIsQuerying(true);
        try {
            setQueryStageLabel(queryMode === "agent" ? "Initialisation du mode Agent…" : "Initialisation du mode Question…");
            setQueryStepStates([]);

            // Only use stores that have a real Google store (name != null)
            const existingStoreNames = allStores.filter(s => s.storeExists !== false && s.name).map(s => s.name as string);

            const storeNames = selectedStoreNames.length > 0 ? selectedStoreNames : existingStoreNames;

            // Resolve resource names → display names so Gemini can identify documents
            const selectedDocumentDisplayNames = allStores
                .flatMap(s => s.documents ?? [])
                .filter(d => selectedDocumentNames.includes(d.name))
                .map(d => d.displayName)
                .filter(Boolean);

            const started = await startFileSearchQueryJob(storeNames, selectedDocumentDisplayNames, query, token, currentHistory, queryMode, queryModel);

            let result: any = null;
            for (let i = 0; i < 600; i++) {
                await new Promise(r => setTimeout(r, 1200));
                const status = await getFileSearchQueryJobStatus(started.jobId, token);
                if (status.stageLabel) {
                    setQueryStageLabel(status.stageLabel);
                }
                if (Array.isArray(status.stepStates)) {
                    setQueryStepStates(status.stepStates);
                } else if (Array.isArray(status.steps)) {
                    setQueryStepStates(
                        status.steps.map(step => ({ name: String(step || "").trim(), status: "pending" as const, result: "" })).filter(s => !!s.name)
                    );
                }
                if (status.status === "done") {
                    result = status.result;
                    break;
                }
                if (status.status === "error") {
                    throw new Error(status.error || "Erreur d'exécution de l'agent");
                }
            }

            if (!result) {
                throw new Error("Timeout: l'agent a mis trop de temps à répondre");
            }

            const modelMsg: ChatMessage = {
                role: "model",
                parts: [{ text: result.text }],
                groundingChunks: result.groundingChunks
            };
            const finalHistory = [...nextHistory, modelMsg];
            setChatHistory(finalHistory);

            // Auto-save conversation after each model reply
            const messagesForSave = finalHistory.map(m => ({
                role: m.role,
                text: m.parts[0]?.text ?? "",
                groundingChunks: m.groundingChunks
            }));

            // Resolve store display names (human-readable) for storage.
            // Use selectedStoreNames (explicit selection), NOT the expanded storeNames,
            // so that "no selection" (= search all) is preserved as an empty list on restore.
            const activeStoreDislayNames = allStores.filter(s => selectedStoreNames.includes(s.name ?? "")).map(s => s.displayName);

            try {
                const saved = await saveConversation(messagesForSave, activeStoreDislayNames, selectedDocumentDisplayNames, token, activeConversationId);
                setActiveConversationId(saved.id);
                setLastSavedAt(saved.createdAt);
            } catch {
                /* non-blocking — history save failure must not disrupt chat */
            }
        } catch (e: any) {
            setError("Erreur lors de la recherche : " + e.message);
        } finally {
            setQueryStageLabel(undefined);
            setQueryStepStates([]);
            setIsQuerying(false);
        }
    };

    const handleNewChat = () => {
        setChatHistory([]);
        setActiveConversationId(undefined);
        setLastSavedAt(undefined);
    };

    const handleSelectConversation = async (id: number) => {
        try {
            const conv = await getConversation(id, token);
            const restored: ChatMessage[] = conv.messages.map((m: any) => ({
                role: m.role as "user" | "model",
                parts: [{ text: m.text }],
                groundingChunks: m.groundingChunks
            }));
            setChatHistory(restored);
            setActiveConversationId(conv.id);
            setLastSavedAt(conv.createdAt);

            // Restore store/document selection from the saved display names
            const allDocs = allStores.flatMap(s => s.documents ?? []);

            const restoredStoreNames = conv.storeNames.map(dn => allStores.find(s => s.displayName === dn)?.name).filter((n): n is string => !!n);

            const restoredDocNames = conv.documentNames.map(dn => allDocs.find(d => d.displayName === dn)?.name).filter((n): n is string => !!n);

            setSelectedStoreNames(restoredStoreNames);
            setSelectedDocumentNames(restoredDocNames);
        } catch (e: any) {
            setError("Impossible de charger la conversation : " + e.message);
        }
    };

    if (loadingAuth) {
        return (
            <div className={styles.loadingScreen}>
                <Spinner size={SpinnerSize.large} label="Initialisation de la session…" />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.subHeader}>
                <h1 className={styles.pageTitle}>Documentation Technique</h1>
                <p className={styles.pageSubtitle}>Recherche sémantique dans vos documentations techniques et schémas électriques via Google RAG</p>
            </div>

            {error && (
                <MessageBar messageBarType={MessageBarType.error} isMultiline={false} onDismiss={() => setError(null)} className={styles.errorBar}>
                    {error}
                </MessageBar>
            )}

            <div className={styles.tabsContainer}>
                <Pivot
                    styles={{
                        root: { borderBottom: "1px solid #e1dfdd" },
                        link: { height: 44, fontSize: 14 }
                    }}
                    onLinkClick={item => {
                        if (item?.props.itemIcon === "Search") {
                            loadStores();
                        }
                    }}
                >
                    <PivotItem headerText="Recherche" itemIcon="Search">
                        <div className={styles.searchTabContainer}>
                            <AtaHistoryPanel
                                token={token}
                                activeId={activeConversationId}
                                savedAt={lastSavedAt}
                                onSelect={handleSelectConversation}
                                onNewChat={handleNewChat}
                                collapsed={historyCollapsed}
                                onToggleCollapse={() => setHistoryCollapsed(v => !v)}
                            />
                            <div className={styles.chatContainer}>
                                <AtaChatInterface
                                    history={chatHistory}
                                    isQueryLoading={isQuerying}
                                    queryStageLabel={queryStageLabel}
                                    queryStepStates={queryStepStates}
                                    queryMode={queryMode}
                                    onChangeQueryMode={setQueryMode}
                                    queryModel={queryModel}
                                    onChangeQueryModel={setQueryModel}
                                    onSendMessage={handleSendMessage}
                                    onNewChat={handleNewChat}
                                    availableStores={allStores}
                                    selectedStoreNames={selectedStoreNames}
                                    selectedDocumentNames={selectedDocumentNames}
                                    onToggleStore={handleToggleStore}
                                    onToggleDocument={handleToggleDocument}
                                />
                            </div>
                        </div>
                    </PivotItem>
                    {(permissions?.isDev || permissions?.canManageDocuments) && (
                        <PivotItem headerText="Gestion des documents" itemIcon="FolderList">
                            <div className={styles.mgmtContainer}>
                                <AtaManagementView permissions={permissions} token={token} onError={setError} onDocumentsChanged={loadStores} />
                            </div>
                        </PivotItem>
                    )}
                </Pivot>
            </div>
        </div>
    );
};

export default AtaPage;
